// priceTicker.js — Servicio de datos para gráfico de velas
// Actualiza precio cada 20 minutos consultando el backend
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

class PriceTicker {
  constructor() {
    this.subscribers = [];
    this.candleHistoryByTimeframe = {};
    this.currentCandleByTimeframe = {};
    this.fetchIntervalId = null;
    this.tickIntervalId = null;
    this.PRECIO_OFICIAL = 7.50;
    this.latestPrice = null;
    this.isInitialized = false;
    this.currentTimeframe = '1d';
    this.FETCH_INTERVAL_MS = 20 * 60 * 1000; // Refrescar API cada 20 minutos
    this.TICK_INTERVAL_MS = 30 * 1000;        // Actualizar vela visual cada 30 seg

    // Timeframes disponibles (valor → configuración)
    this.timeframeConfig = {
      '30m': { interval: 30 * 60,          label: '30 min', daysBack: 7,   maxCandles: 336  },
      '1h':  { interval: 60 * 60,          label: '1 hora', daysBack: 14,  maxCandles: 336  },
      '1d':  { interval: 24 * 60 * 60,     label: '1 día',  daysBack: 90,  maxCandles: 90   },
      '1w':  { interval: 7 * 24 * 60 * 60, label: '1 sem',  daysBack: 365, maxCandles: 52   },
      '1M':  { interval: 30 * 24 * 60 * 60,label: '1 mes',  daysBack: 730, maxCandles: 24   },
    };
  }

  // ─── Inicialización: carga histórico desde el backend ────────────────────────
  async initialize(timeframe = '1d', retryCount = 0) {
    try {
      const config = this.timeframeConfig[timeframe];
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(`${API_BASE_URL}/prices/historical-candlestick/`, {
        params: { timeframe, limit: config.maxCandles * 10, zone: this.currentZone || 'norte' },
        headers,
        timeout: 25000, // 25s — da tiempo al free tier de Render para despertar
      });

      const rawData = response.data;

      if (rawData && rawData.length > 0) {
        this.candleHistoryByTimeframe[timeframe] = this.convertToCandleData(rawData, timeframe);

        const last = rawData[rawData.length - 1];
        const lastPrice = parseFloat(last.price_per_box);
        const lastTime = Math.floor(new Date(last.date).getTime() / 1000);

        this.currentCandleByTimeframe[timeframe] = {
          time: this.getNextCandleTime(lastTime, timeframe),
          open: lastPrice,
          high: lastPrice,
          low: lastPrice,
          close: lastPrice,
        };

        this.latestPrice = last;
      } else {
        this._initFallbackCandle(timeframe);
      }

      this.isInitialized = true;
    } catch (error) {
      const isNetworkError = !error.response; // sin respuesta del servidor
      if (isNetworkError && retryCount < 3) {
        // Render free tier: el servicio puede tardar hasta ~60s en arrancar.
        // Reintentar en 10s, 25s y 50s.
        const delays = [10000, 25000, 50000];
        const delay = delays[retryCount];
        setTimeout(async () => {
          await this.initialize(timeframe, retryCount + 1);
          this.notifyHistoricalData();
        }, delay);
      } else {
        console.error(`Error inicializando ticker [${timeframe}]:`, error.message);
      }
      this._initFallbackCandle(timeframe);
      this.isInitialized = true;
    }
  }

  // ─── Carga el último precio desde el backend ─────────────────────────────────
  async fetchLatestPrice() {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/prices/latest/`, { headers });
      this.latestPrice = response.data;
    } catch (error) {
      // Silencioso: el gráfico sigue con el último precio conocido
    }
  }

  // ─── Conversión de registros planos a OHLC + volumen ─────────────────────────
  // Volume = cantidad de reportes que cayeron dentro del bucket de tiempo de
  // la vela (proxy de actividad del mercado).
  convertToCandleData(rawData, timeframe) {
    const candleMap = new Map();

    rawData.forEach(point => {
      const ts = Math.floor(new Date(point.date).getTime() / 1000);
      const t  = this.roundToInterval(ts, timeframe);
      const p  = parseFloat(point.price_per_box);

      if (candleMap.has(t)) {
        const c = candleMap.get(t);
        c.high   = Math.max(c.high, p);
        c.low    = Math.min(c.low, p);
        c.close  = p;
        c.volume = (c.volume || 1) + 1;
      } else {
        candleMap.set(t, { time: t, open: p, high: p, low: p, close: p, volume: 1 });
      }
    });

    return Array.from(candleMap.values()).sort((a, b) => a.time - b.time);
  }

  roundToInterval(ts, timeframe) {
    const interval = this.timeframeConfig[timeframe].interval;
    return Math.floor(ts / interval) * interval;
  }

  getNextCandleTime(ts, timeframe) {
    const interval = this.timeframeConfig[timeframe].interval;
    return this.roundToInterval(ts, timeframe) + interval;
  }

  // ─── Inicio / parada ─────────────────────────────────────────────────────────
  async start(timeframe = '1d', zone = 'norte') {
    this.currentTimeframe = timeframe;
    this.currentZone = zone;
    await this.initialize(timeframe);
    this.notifyHistoricalData();

    // Tick visual cada 30 segundos
    this.tickIntervalId = setInterval(() => this._tick(), this.TICK_INTERVAL_MS);

    // Re-fetch del API cada 20 minutos
    this.fetchIntervalId = setInterval(async () => {
      await this.fetchLatestPrice();
      // Re-inicializar para actualizar el histórico completo
      await this.initialize(this.currentTimeframe);
      this.notifyHistoricalData();
    }, this.FETCH_INTERVAL_MS);
  }

  stop() {
    clearInterval(this.tickIntervalId);
    clearInterval(this.fetchIntervalId);
    this.tickIntervalId = null;
    this.fetchIntervalId = null;
  }

  async changeTimeframe(newTimeframe) {
    if (!this.timeframeConfig[newTimeframe]) return;
    this.currentTimeframe = newTimeframe;
    await this.initialize(newTimeframe);
    this._notifyTimeframeChange(newTimeframe);
  }

  async changeZone(newZone) {
    this.currentZone = newZone;
    // Limpiar caché para forzar recarga con la nueva zona
    this.candleHistoryByTimeframe = {};
    this.currentCandleByTimeframe = {};
    await this.initialize(this.currentTimeframe);
    this.notifyHistoricalData();
  }

  // ─── Suscriptores ────────────────────────────────────────────────────────────
  subscribe(callback) {
    this.subscribers.push(callback);

    const history = this.candleHistoryByTimeframe[this.currentTimeframe] || [];
    if (history.length > 0) {
      callback({ type: 'history', data: history, timeframe: this.currentTimeframe });
    }
    const current = this.currentCandleByTimeframe[this.currentTimeframe];
    if (current) {
      callback({ type: 'update', data: current, timeframe: this.currentTimeframe });
    }

    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
    };
  }

  notifyHistoricalData() {
    const history = this.candleHistoryByTimeframe[this.currentTimeframe] || [];
    this._notify({ type: 'history', data: history, timeframe: this.currentTimeframe });
  }

  _notifyTimeframeChange(timeframe) {
    const history = this.candleHistoryByTimeframe[timeframe] || [];
    const current = this.currentCandleByTimeframe[timeframe] || null;
    this._notify({ type: 'timeframe_change', timeframe, history, currentCandle: current });
  }

  _notify(data) {
    this.subscribers.forEach(cb => {
      try { cb(data); } catch (e) { /* ignorar errores de suscriptores */ }
    });
  }

  // ─── Tick periódico (actualiza vela actual con el último precio) ──────────────
  _tick() {
    const now = Math.floor(Date.now() / 1000);
    Object.keys(this.candleHistoryByTimeframe).forEach(tf => {
      this._updateCandleForTimeframe(tf, now);
    });

    const current = this.currentCandleByTimeframe[this.currentTimeframe];
    if (current) {
      this._notify({ type: 'update', data: current, timeframe: this.currentTimeframe });
    }
  }

  _updateCandleForTimeframe(timeframe, now) {
    const candleTime = this.roundToInterval(now, timeframe);

    if (!this.currentCandleByTimeframe[timeframe]) {
      const p = this.latestPrice
        ? parseFloat(this.latestPrice.price_per_box)
        : this.PRECIO_OFICIAL;
      this.currentCandleByTimeframe[timeframe] = {
        time: candleTime, open: p, high: p, low: p, close: p,
      };
    }

    const candle = this.currentCandleByTimeframe[timeframe];

    // Cerrar vela si el período terminó
    if (candleTime > candle.time) {
      const history = this.candleHistoryByTimeframe[timeframe];
      const idx = history.findIndex(c => c.time === candle.time);
      if (idx >= 0) {
        history[idx] = { ...candle };
      } else {
        history.push({ ...candle });
        const max = this.timeframeConfig[timeframe].maxCandles;
        if (history.length > max) history.splice(0, history.length - max);
      }
      // Abrir nueva vela
      this.currentCandleByTimeframe[timeframe] = {
        time: candleTime,
        open: candle.close,
        high: candle.close,
        low: candle.close,
        close: candle.close,
      };
    }

    // Actualizar vela con precio más reciente
    if (this.latestPrice) {
      const price = parseFloat(this.latestPrice.price_per_box);
      const priceTs = Math.floor(new Date(this.latestPrice.date).getTime() / 1000);
      const priceCandleTime = this.roundToInterval(priceTs, timeframe);
      const c = this.currentCandleByTimeframe[timeframe];
      if (priceCandleTime === c.time) {
        c.high  = Math.max(c.high, price);
        c.low   = Math.min(c.low, price);
        c.close = price;
      }
    }
  }

  // ─── Fallback cuando no hay datos ────────────────────────────────────────────
  _initFallbackCandle(timeframe) {
    const now = Math.floor(Date.now() / 1000);
    const p = this.latestPrice
      ? parseFloat(this.latestPrice.price_per_box)
      : this.PRECIO_OFICIAL;
    this.currentCandleByTimeframe[timeframe] = {
      time: this.roundToInterval(now, timeframe), open: p, high: p, low: p, close: p,
    };
    this.candleHistoryByTimeframe[timeframe] = [this.currentCandleByTimeframe[timeframe]];
  }

  // ─── Helpers públicos ─────────────────────────────────────────────────────────
  getAvailableTimeframes() {
    return Object.keys(this.timeframeConfig).map(tf => ({
      value: tf,
      label: this.timeframeConfig[tf].label,
    }));
  }

  getCurrentTimeframeConfig() {
    return this.timeframeConfig[this.currentTimeframe];
  }

  updateLatestPrice(latestPrice) {
    this.latestPrice = latestPrice;
  }
}

const priceTicker = new PriceTicker();
export default priceTicker;
